"use client";

import { Eye } from "lucide-react";
import {
  INVESTOR_READ_ONLY_MESSAGE,
  useIsInvestorAccount,
} from "@/vybe-trader-client/account-information/hooks/useAccountInformation";

/**
 * "Investor (read-only)" chip for the account strip / account selector. Same neutral chip surface as
 * GatewayChip (colour discipline: status stays on dots), with an eye glyph so it reads as view-only.
 * Renders nothing unless the connected session logged in with the investor password.
 */
export function InvestorBadge({ className = "" }: { className?: string }) {
  const isInvestor = useIsInvestorAccount();
  if (!isInvestor) return null;
  return (
    <span
      data-testid="investor-badge"
      title={INVESTOR_READ_ONLY_MESSAGE}
      aria-label={INVESTOR_READ_ONLY_MESSAGE}
      className={`inline-flex items-center gap-1 h-[15px] px-1.5 rounded-full bg-muted border border-border text-muted-foreground font-mono text-[9px] font-semibold tracking-wide shrink-0 whitespace-nowrap ${className}`}
    >
      <Eye className="size-2.5" aria-hidden="true" />
      Investor (read-only)
    </span>
  );
}

/**
 * Replaces an order ticket body on an investor session: one muted line instead of controls the server
 * would refuse. Dense by default (11px) to sit inside the desktop dock; `comfortable` for mobile.
 */
export function InvestorReadOnlyNotice({ comfortable = false }: { comfortable?: boolean }) {
  return (
    <div
      data-testid="investor-read-only-notice"
      role="status"
      className={`flex items-center justify-center gap-1.5 text-muted-foreground ${
        comfortable ? "text-sm p-4" : "text-[11px] p-2"
      }`}
    >
      <Eye className={comfortable ? "size-4" : "size-3"} aria-hidden="true" />
      <span>{INVESTOR_READ_ONLY_MESSAGE}</span>
    </div>
  );
}

export default InvestorBadge;
